import {useState, useEffect, useMemo, useRef} from "react";
import {useReactToPrint} from "react-to-print";
import {
  Activity,
  MousePointer,
  Clock,
  ChevronRight,
  Play,
  Download,
  FileText,
  ArrowUpDown,
  Users,
} from "lucide-react";

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
  </div>
);

// StatCard: Responsive layout to prevent text clipping
const StatCard = ({icon, label, value, color}) => (
  <div className="bg-white p-3 md:p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 h-full min-h-[120px] md:min-h-[130px] w-full hover:shadow-md transition-shadow print:break-inside-avoid print:shadow-none print:border-gray-200">
    <div
      className={`p-2 md:p-3 rounded-2xl shrink-0 ${color} flex items-center justify-center`}
    >
      {icon}
    </div>
    <div className="flex flex-col min-w-0">
      <p className="text-[9px] md:text-xs text-gray-400 font-bold uppercase tracking-wider leading-tight">
        {label}
      </p>
      <p className="text-lg md:text-2xl font-black text-gray-900 leading-tight mt-1">
        {value}
      </p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [userData, setUserData] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("time");

  // Changed default state from "all" to 1 (representing February)
  const [activeFilter, setActiveFilter] = useState(1);

  const mainRef = useRef(null);

  const downloadPDF = useReactToPrint({
    contentRef: mainRef,
    pageStyle: `
      @page { 
        margin: 15mm; 
        size: A4;
      }
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
        main, .dashboard-main {
          position: static !important;
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
          display: block !important;
          padding: 0 !important;
        }
        .print\\:hidden { display: none !important; }
        .print\\:break-inside-avoid { 
          break-inside: avoid !important; 
          page-break-inside: avoid !important;
        }
        .stat-grid-print { 
            display: grid !important; 
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
        }
      }
    `,
  });

  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (args[0]?.includes?.("react-to-print.mjs")) return;
      originalWarn(...args);
    };
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  const setMonthRange = (offset) => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() - offset + 1, 0);

    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(lastDay.toISOString().split("T")[0]);
    setActiveFilter(offset);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
        };

        const [userRes, videoRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contacts`, {
            method: "GET",
            headers,
          }),
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`, {
            method: "GET",
            headers,
          }),
        ]);

        const userDataJson = await userRes.json();
        const videoDataJson = await videoRes.json();

        if (videoDataJson.result === "success") {
          setVideos(videoDataJson.data);
        }

        if (userDataJson.result === "success" && userDataJson.data.length > 0) {
          setUserData(userDataJson.data);
          setSelectedUser(userDataJson.data[0]);
          // Initialize with the default range (February)
          const now = new Date();
          const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
          setStartDate(firstDay.toISOString().split("T")[0]);
          setEndDate(lastDay.toISOString().split("T")[0]);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const monthButtons = useMemo(() => {
    return [1, 2, 3].map((offset) => {
      const d = new Date();
      d.setMonth(d.getMonth() - offset);
      return {
        label: d.toLocaleString("default", {month: "long"}),
        offset: offset,
      };
    });
  }, []);

  const getVideoInfo = (id) => {
    const video = videos.find((v) => v.id.toString() === id.toString());
    return video
      ? {
          title: video.title,
          category: video.category,
          length: video.videoLength,
        }
      : {title: `Video #${id}`, category: "Unknown Category", length: 0};
  };

  const aggregatedStats = useMemo(() => {
    if (!selectedUser) return null;

    const activityTotals = {
      clicks: 0,
      secondsActive: 0,
      mouseMoves: 0,
      totalVideoSeconds: 0,
    };

    const isWithinRange = (dateStr) => {
      if (!startDate && !endDate) return true;
      const date = new Date(dateStr);
      const start = startDate ? new Date(startDate) : new Date("1970-01-01");
      const end = endDate ? new Date(endDate) : new Date("2099-12-31");
      return date >= start && date <= end;
    };

    if (selectedUser.activity) {
      Object.entries(selectedUser.activity).forEach(([date, day]) => {
        if (isWithinRange(date)) {
          activityTotals.clicks += day.clicks || 0;
          activityTotals.secondsActive += day.secondsActive || 0;
          activityTotals.mouseMoves += day.mouseMoves || 0;
          activityTotals.totalVideoSeconds += day.totalVideoSeconds || 0;
        }
      });
    }

    const historyTotals = {};
    if (selectedUser.watch_history) {
      Object.entries(selectedUser.watch_history).forEach(([videoId, data]) => {
        const totalSecs = Object.entries(data.dailyBreakdown || {}).reduce(
          (sum, [date, val]) => (isWithinRange(date) ? sum + val : sum),
          0,
        );
        historyTotals[videoId] = totalSecs;
      });
    }

    const sortedHistory = Object.entries(historyTotals).sort((a, b) => {
      if (sortBy === "name") {
        const titleA = getVideoInfo(a[0]).title.toLowerCase();
        const titleB = getVideoInfo(b[0]).title.toLowerCase();
        return titleA.localeCompare(titleB);
      }
      return b[1] - a[1];
    });

    return {
      activityTotals,
      sortedHistory,
      isMet: activityTotals.totalVideoSeconds >= 80 * 3600,
    };
  }, [selectedUser, startDate, endDate, sortBy, videos, getVideoInfo]);

  const formatDate = (isoString) => new Date(isoString).toLocaleString();

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${hours > 0 ? hours + "h " : ""}${minutes > 0 ? minutes + "m " : ""}${seconds}s`;
  };

  const downloadCSV = () => {
    const {activityTotals, sortedHistory} = aggregatedStats;
    const rows = [
      ["Metric", "Value"],
      [
        "Customer",
        `${selectedUser.customer_firstname} ${selectedUser.customer_lastname}`,
      ],
      ["Email", selectedUser.customer_id],
      ["Date Range", `${startDate || "Start"} to ${endDate || "End"}`],
      ["Total Clicks", activityTotals.clicks],
      ["Interactions", activityTotals.mouseMoves],
      ["Platform Time", formatTime(activityTotals.secondsActive)],
      [
        "Hours Reported (Playtime)",
        formatTime(activityTotals.totalVideoSeconds),
      ],
      ["", ""],
      ["Video ID", "Video Title", "Category", "Time Watched (Seconds)"],
    ];

    sortedHistory.forEach(([id, secs]) => {
      const info = getVideoInfo(id);
      rows.push([
        id,
        `"${info.title.replace(/"/g, '""')}"`,
        info.category,
        secs.toFixed(2),
      ]);
    });

    const csvContent = rows.map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `analytics_${selectedUser.customer_id}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading)
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <OrbitLoader />
      </div>
    );
  if (!selectedUser || !aggregatedStats)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-gray-500">No user data available.</p>
      </div>
    );

  const {activityTotals, sortedHistory, isMet} = aggregatedStats;

  return (
    <div className="flex flex-col lg:flex-row h-screen w-full font-sans print:block print:h-auto overflow-hidden bg-gray-50">
      <nav className="hidden lg:flex lg:w-[20%] bg-white border-r border-gray-200 flex-col print:hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-black uppercase text-xs text-gray-400">Users</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {userData.map((user) => (
            <button
              key={user.customer_id}
              onClick={() => setSelectedUser(user)}
              className={`w-full text-left p-3 rounded-xl transition-all flex items-center justify-between group ${
                selectedUser?.customer_id === user.customer_id
                  ? "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                  : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              <div className="truncate pr-2">
                <p className="text-sm font-bold truncate">
                  {user.customer_firstname} {user.customer_lastname}
                </p>
                <p className="text-[10px] opacity-70 truncate">
                  {user.customer_id}
                </p>
              </div>
              <ChevronRight
                size={14}
                className={
                  selectedUser?.customer_id === user.customer_id
                    ? "opacity-100"
                    : "opacity-0"
                }
              />
            </button>
          ))}
        </div>
      </nav>

      <main
        ref={mainRef}
        className="dashboard-main flex-1 overflow-y-auto p-4 md:p-10 print:overflow-visible print:p-0 print:bg-white"
      >
        <header className="mb-8 flex flex-col gap-6 print:pb-8 print:border-b print:border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900">
                Analytics Overview
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Viewing data for{" "}
                <span className="font-medium text-gray-800">
                  {selectedUser.customer_firstname}{" "}
                  {selectedUser.customer_lastname}
                </span>
              </p>
            </div>

            <div className="lg:hidden flex items-center gap-2 bg-white p-2 rounded-xl border border-gray-200 shadow-sm print:hidden">
              <Users size={18} className="text-gray-400 ml-1" />
              <select
                className="bg-transparent text-sm font-bold w-full focus:outline-none"
                value={selectedUser.customer_id}
                onChange={(e) =>
                  setSelectedUser(
                    userData.find((u) => u.customer_id === e.target.value),
                  )
                }
              >
                {userData.map((u) => (
                  <option key={u.customer_id} value={u.customer_id}>
                    {u.customer_firstname} {u.customer_lastname} (
                    {u.customer_id})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 print:hidden">
              <button
                onClick={downloadCSV}
                className="flex-1 md:flex-none justify-center p-2.5 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 flex items-center gap-2 text-sm font-bold shadow-sm"
              >
                <Download size={16} />
                CSV
              </button>
              <button
                onClick={downloadPDF}
                className="flex-1 md:flex-none justify-center p-2.5 bg-gray-800 border border-gray-800 rounded-xl text-white hover:bg-gray-900 flex items-center gap-2 text-sm font-bold shadow-sm"
              >
                <FileText size={16} />
                PDF
              </button>
            </div>
          </div>

          <div
            className={`p-6 rounded-2xl border flex items-center justify-between transition-colors shadow-sm ${
              isMet
                ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                : "bg-rose-50 border-rose-100 text-rose-800"
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`p-3 rounded-xl ${isMet ? "bg-emerald-500" : "bg-rose-500"}`}
              >
                <Clock size={24} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest opacity-70">
                  Monthly Performance Status
                </p>
                <h3 className="text-xl font-bold flex items-baseline gap-1">
                  {(() => {
                    const totalSeconds = activityTotals.totalVideoSeconds;
                    const hours = Math.floor(totalSeconds / 3600);
                    const minutes = Math.floor((totalSeconds % 3600) / 60);

                    return (
                      <>
                        <span>{hours}</span>
                        <span className="text-sm font-black uppercase opacity-60 mr-2">
                          h
                        </span>
                        <span>{minutes}</span>
                        <span className="text-sm font-black uppercase opacity-60">
                          m
                        </span>
                        <span className="ml-2 text-sm font-medium opacity-50 italic">
                          Logged
                        </span>
                      </>
                    );
                  })()}
                </h3>
              </div>
            </div>

            <div className="hidden md:flex flex-col items-end">
              <span
                className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider ${
                  isMet
                    ? "bg-emerald-200 text-emerald-900"
                    : "bg-rose-200 text-rose-900"
                }`}
              >
                {isMet ? "Target Met" : "Below Target"}
              </span>
              <p className="text-[10px] mt-2 font-bold opacity-60">
                Benchmark: 80.0 Hours
              </p>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end print:hidden">
            <div className="flex items-center gap-1 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm w-full sm:w-auto">
              {monthButtons.map((month) => (
                <button
                  key={month.offset}
                  onClick={() => setMonthRange(month.offset)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                    activeFilter === month.offset
                      ? "bg-blue-600 text-white"
                      : "text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {month.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8 items-stretch stat-grid-print">
          <StatCard
            icon={<MousePointer size={18} className="text-orange-600" />}
            label="Clicks"
            value={activityTotals.clicks}
            color="bg-orange-100"
          />
          <StatCard
            icon={<Activity size={18} className="text-blue-600" />}
            label="Interactions"
            value={activityTotals.mouseMoves.toLocaleString()}
            color="bg-blue-100"
          />
          <StatCard
            icon={<Clock size={18} className="text-green-600" />}
            label="Active Time"
            value={formatTime(activityTotals.secondsActive)}
            color="bg-green-100"
          />
          <StatCard
            icon={<Play size={18} className="text-purple-600" />}
            label="Video Playtime"
            value={formatTime(activityTotals.totalVideoSeconds)}
            color="bg-purple-100"
          />
        </div>

        <div className="bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-gray-100 mb-6 print:shadow-none print:border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold">Watch History</h2>
              <p className="text-gray-400 text-sm">
                {startDate || endDate
                  ? `Filtered Results`
                  : `Cumulative Engagement`}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 print:hidden self-start">
              <ArrowUpDown size={14} className="text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-transparent text-xs font-bold text-gray-600 focus:outline-none cursor-pointer uppercase"
              >
                <option value="time">Sort by Time</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {sortedHistory.length > 0 ? (
              sortedHistory.map(([id, totalSeconds]) => {
                const videoInfo = getVideoInfo(id);
                return (
                  <div
                    className="flex flex-col p-4 rounded-xl border border-gray-100 bg-white print:break-inside-avoid print:border-gray-300"
                    key={id}
                  >
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">
                          {videoInfo.title}
                        </p>
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 px-2 py-0.5 rounded">
                          {videoInfo.category}
                        </span>
                      </div>
                      <span className="text-blue-600 text-sm font-black whitespace-nowrap">
                        {formatTime(totalSeconds)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(
                            (totalSeconds / (videoInfo.length || 1)) * 100,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-400 italic text-sm py-10 text-center">
                No watch history found for this period.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center text-[11px] bg-white px-4 py-3 rounded-xl border border-gray-100 text-gray-400 gap-2 print:border-gray-200">
          <span className="font-bold uppercase tracking-wider">
            System Analytics Report
          </span>
          <span>
            Last Activity:{" "}
            {selectedUser.timestamp
              ? formatDate(selectedUser.timestamp)
              : "N/A"}
          </span>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
